// reCAPTCHA v3 helper. The site key is exposed to the page via REACT_ENV_VARIABLES
// (see external_scripts/_react_env_variables_script.html.erb), with the
// recaptcha-site-key meta tag kept as a fallback. Google's script is loaded
// lazily and reused across calls.

const SITE_KEY_META = 'recaptcha-site-key'
const SCRIPT_ID = 'recaptcha-v3-script'

let scriptPromise = null

export const getRecaptchaSiteKey = () => {
  const envKey =
    typeof window !== 'undefined' &&
    window.REACT_ENV_VARIABLES &&
    window.REACT_ENV_VARIABLES.RECAPTCHA_V3_SITE_KEY
  if (envKey) return envKey

  if (typeof document === 'undefined') return ''
  const meta = document.querySelector(`meta[name="${SITE_KEY_META}"]`)
  return (meta && meta.getAttribute('content')) || ''
}

const loadRecaptchaScript = (siteKey) => {
  if (scriptPromise) return scriptPromise

  scriptPromise = new Promise((resolve, reject) => {
    if (window.grecaptcha) {
      resolve()
      return
    }

    const existing = document.getElementById(SCRIPT_ID)
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', reject)
      return
    }

    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = reject
    document.head.appendChild(script)
  })

  return scriptPromise
}

// Warms the reCAPTCHA script so a token can be produced quickly at submit time.
// Safe to call repeatedly and when no site key is configured.
export const preloadRecaptcha = () => {
  const siteKey = getRecaptchaSiteKey()
  if (!siteKey) return
  loadRecaptchaScript(siteKey).catch(() => {})
}

// Returns a reCAPTCHA v3 token for the given action. Falls back to the backend's
// development bypass token ('dev') when no site key is configured so local dev
// keeps working without keys. Returns null if the script fails to produce a
// token (the backend will then reject the request).
export const executeRecaptcha = async (action = 'submit') => {
  const siteKey = getRecaptchaSiteKey()
  if (!siteKey) return 'dev'

  try {
    await loadRecaptchaScript(siteKey)
    await new Promise((resolve) => window.grecaptcha.ready(resolve))
    return await window.grecaptcha.execute(siteKey, { action })
  } catch (error) {
    return null
  }
}
